import { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronsDown, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import styles from "./TermsModal.module.css";
import { getAgreementParagraphs } from "../../../legal/agreementContent";
import RichText from "../../../legal/RichText";

// Tolerance (px) for treating the scroll position as "the bottom".
const SCROLL_BOTTOM_THRESHOLD = 24;

const TermsModal = ({ isOpen, onClose, onAccept }) => {
  const { t, i18n } = useTranslation();
  const paragraphs = getAgreementParagraphs(i18n.language);
  const scrollRef = useRef(null);
  const [atBottom, setAtBottom] = useState(false);

  // Each time the modal opens: start at the top, and if the content is short
  // enough not to scroll, treat it as already read so Accept isn't unreachable.
  useEffect(() => {
    if (!isOpen) return;
    setAtBottom(false);
    const el = scrollRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      if (el.scrollHeight - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD) {
        setAtBottom(true);
      } else {
        el.scrollTop = 0;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // Close on Escape and lock background scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD) {
      setAtBottom(true);
    }
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setAtBottom(true);
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={t("auth.terms.modalTitle")}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 className={styles.title}>{t("auth.terms.modalTitle")}</h3>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className={styles.body} ref={scrollRef} onScroll={handleScroll}>
          {paragraphs.map((paragraph, i) => (
            <p key={i} className={styles.paragraph}>
              <RichText text={paragraph} />
            </p>
          ))}
        </div>

        <div className={styles.footer}>
          {!atBottom && (
            <button type="button" className={styles.scrollBtn} onClick={scrollToBottom}>
              <FiChevronsDown size={16} /> {t("auth.terms.scrollToBottom")}
            </button>
          )}
          <div className={styles.footerRight}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className={styles.acceptBtn}
              onClick={onAccept}
              disabled={!atBottom}
              title={!atBottom ? t("auth.terms.readFirstHint") : undefined}
            >
              <FiCheck size={16} /> {t("auth.terms.accept")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
